import React, { useCallback } from 'react'
import Modal from 'shared/ui/Modal/Modal'
import DatePickerInput from 'shared/components/DatePickerInput/DatePickerInput'
import LoaderImg from 'shared/components/LoaderImg/LoaderImg'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg'
import { ReactComponent as CalendarCheckIcon } from 'assets/icons/calendar_check.svg'
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow_short_right.svg'
import { ReactComponent as HierarchyIcon } from 'assets/icons/arrow-diagram.svg'
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg'
import Header from '../Header/Header'
import useSchoolScheduleOptions from './hooks/useSchoolScheduleOptions'
import s from './SchoolScheduleOptions.module.scss'

const SchoolScheduleOptions = ({ goBack, onAcademicCalendarUpdated }) => {
	const handleCalendarUpdated = useCallback(
		data => {
			onAcademicCalendarUpdated?.(data)
		},
		[onAcademicCalendarUpdated],
	)

	const {
		draft,
		confirmOpen,
		saved,
		isValid,
		hasChanges,
		totalDays,
		dateRangeLabel,
		loadStatus,
		loadError,
		saveStatus,
		saveError,
		updateDraft,
		requestSave,
		closeConfirm,
		confirmSave,
		reload,
	} = useSchoolScheduleOptions({ onCalendarUpdated: handleCalendarUpdated })

	const isLoading = loadStatus === 'idle' || loadStatus === 'loading'
	const hasLoadError = loadStatus === 'error'
	const canEdit = loadStatus === 'success'
	const isSaving = saveStatus === 'saving'
	const showInvalid = canEdit && Boolean(draft.startDate || draft.endDate) && !isValid
	const dateFieldClass = `${s.dateField} ${showInvalid ? s.dateFieldInvalid : ''}`.trim()

	return (
		<>
			<Header
				title="School preferences"
				description="Set the dates Kandor uses for your school's academic cycle."
				onBack={goBack}
			/>
			<section className={s.card}>
				<div className={s.heading}>
					<span className={s.icon} aria-hidden="true">
						<CalendarIcon />
					</span>
					<div>
						<h2 className={s.title}>Academic year dates</h2>
						<p className={s.description}>
							Choose the first and last day of your school year.
						</p>
					</div>
				</div>

				{isLoading ? (
					<div className={s.state} aria-busy="true" aria-label="Loading academic calendar">
						<LoaderImg />
					</div>
				) : null}

				{hasLoadError ? (
					<div className={s.state} role="alert">
						<p className={s.error}>
							{loadError || 'Academic calendar could not be loaded.'}
						</p>
						<button type="button" className={s.retryButton} onClick={reload}>
							Try again
						</button>
					</div>
				) : null}

				{canEdit ? (
					<form onSubmit={requestSave} className={s.form}>
						<div className={s.fields}>
							<label className={s.field}>
								<span className={s.fieldLabel}>Start date</span>
								<div className={dateFieldClass}>
									<DatePickerInput
										value={draft.startDate}
										onChange={value => updateDraft('startDate', value)}
										minISO="2000-01-01"
										className={s.dateInput}
										inputClassName={s.dateInputControl}
									/>
								</div>
							</label>
							<span className={s.arrow} aria-hidden="true">
								<ArrowIcon />
							</span>
							<label className={s.field}>
								<span className={s.fieldLabel}>End date</span>
								<div className={dateFieldClass}>
									<DatePickerInput
										value={draft.endDate}
										onChange={value => updateDraft('endDate', value)}
										minISO={draft.startDate || '2000-01-01'}
										className={s.dateInput}
										inputClassName={s.dateInputControl}
									/>
								</div>
							</label>
						</div>
						{showInvalid ? (
							<p className={s.error}>
								The end date must be after the start date on the academic-year timeline.
							</p>
						) : null}
						{saveError ? (
							<p className={s.error} role="alert">
								{saveError}
							</p>
						) : null}
						<div className={s.duration} aria-live="polite">
							<span className={s.durationIcon} aria-hidden="true">
								<CalendarCheckIcon />
							</span>
							<div className={s.durationCopy}>
								<span className={s.durationLabel}>Academic year duration</span>
								<strong className={s.durationValue}>{totalDays} days</strong>
							</div>
							<small className={s.durationRange}>{dateRangeLabel}</small>
						</div>
						<div className={s.footer}>
							<p className={s.footerHint}>
								{saved
									? 'Academic year updated for your organization.'
									: 'Changes apply to the whole school after confirmation.'}
							</p>
							<button
								type="submit"
								className={s.saveButton}
								disabled={!isValid || !hasChanges || isSaving}
							>
								{isSaving ? 'Saving…' : 'Save changes'}
							</button>
						</div>
					</form>
				) : null}
			</section>

			<Modal
				isOpen={confirmOpen}
				onClose={closeConfirm}
				title="Confirm academic year change"
				iconTitle={HierarchyIcon}
				subtitle="This updates the organization-wide academic calendar for your school."
				ariaLabel="Confirm academic year change"
				width={520}
				maxHeight={460}
				compact
				closeButtonClassName={s.modalClose}
			>
				<div className={s.confirm}>
					<div className={s.confirmLead}>
						<span className={s.warning} aria-hidden="true">
							<WarningIcon />
						</span>
						<div>
							<h3 className={s.confirmTitle}>Check the dates before you continue.</h3>
							<p className={s.confirmText}>
								The current academic year will be recalculated immediately. Past
								completed academic years stay unchanged. All counsellors at this school
								will use the updated calendar.
							</p>
						</div>
					</div>
					<div className={s.confirmActions}>
						<button
							type="button"
							className={s.cancelButton}
							onClick={closeConfirm}
							disabled={isSaving}
						>
							Go back
						</button>
						<button
							type="button"
							className={s.confirmButton}
							onClick={confirmSave}
							disabled={isSaving}
						>
							{isSaving ? 'Saving…' : 'Confirm change'}
						</button>
					</div>
				</div>
			</Modal>
		</>
	)
}

export default SchoolScheduleOptions
